import { decodeName } from './decodeName';
import { aggregateSeoFromLeaves, normalizeNodeSeo } from './seoMetrics';
import type { HierarchicalNode, TreeModel, TreeNode } from '../types';

/**
 * Flatten hierarchical JSON into a stable tree model with ids + SEO metrics.
 * Cluster nodes get aggregated volume (sum), average difficulty, and average len from leaves.
 */
export function buildTreeModel(root: HierarchicalNode | null | undefined): TreeModel {
  const nodesById = new Map<string, TreeNode>();
  let counter = 0;

  function walk(
    node: HierarchicalNode,
    parentId: string | null = null,
    depth = 0,
    pathLabels: string[] = []
  ): string {
    const id = `n-${counter++}`;
    const label = decodeName(node.name);
    const path = [...pathLabels, label];
    const childIds: string[] = [];
    const hasChildren = Boolean(node.children?.length);

    const entry: TreeNode = {
      id,
      parentId,
      depth,
      label,
      rawName: node.name,
      path,
      childIds,
      isLeaf: !hasChildren,
      seo: hasChildren ? null : normalizeNodeSeo(node)
    };
    nodesById.set(id, entry);

    (node.children ?? []).forEach(child => {
      const childId = walk(child, id, depth + 1, path);
      childIds.push(childId);
    });

    entry.isLeaf = childIds.length === 0;

    if (!entry.isLeaf) {
      const leafIds = getDescendantIds(nodesById, id).filter(
        descId => nodesById.get(descId)?.isLeaf
      );
      const leaves = leafIds.map(descId => nodesById.get(descId)!.seo);
      entry.seo = aggregateSeoFromLeaves(leaves);
    }

    return id;
  }

  const rootId = root ? walk(root) : null;
  return { nodesById, rootId };
}

export function getDescendantIds(
  nodesById: Map<string, TreeNode>,
  id: string
): string[] {
  const result: string[] = [];
  const stack = [...(nodesById.get(id)?.childIds ?? [])];
  while (stack.length) {
    const current = stack.pop()!;
    result.push(current);
    const node = nodesById.get(current);
    if (node?.childIds?.length) stack.push(...node.childIds);
  }
  return result;
}

export function getAncestorIds(
  nodesById: Map<string, TreeNode>,
  id: string
): string[] {
  const result: string[] = [];
  let current = nodesById.get(id);
  while (current?.parentId) {
    result.push(current.parentId);
    current = nodesById.get(current.parentId);
  }
  return result;
}

export function collectExpandableIds(nodesById: Map<string, TreeNode>): string[] {
  return [...nodesById.values()]
    .filter(node => !node.isLeaf)
    .map(node => node.id);
}

export function defaultExpandedIds(
  nodesById: Map<string, TreeNode>,
  maxDepth = 1
): Set<string> {
  return new Set(
    [...nodesById.values()]
      .filter(node => !node.isLeaf && node.depth <= maxDepth)
      .map(node => node.id)
  );
}

export function findMatchingIds(
  nodesById: Map<string, TreeNode>,
  query: string
): Set<string> {
  const q = query.trim().toLowerCase();
  if (!q) return new Set();
  return new Set(
    [...nodesById.values()]
      .filter(node => node.label.toLowerCase().includes(q))
      .map(node => node.id)
  );
}

/** Ensure every match and its ancestors are expanded. */
export function expandPathsToMatches(
  nodesById: Map<string, TreeNode>,
  matchIds: Set<string> | string[]
): Set<string> {
  const expanded = new Set<string>();
  matchIds.forEach(id => {
    getAncestorIds(nodesById, id).forEach(ancestorId => expanded.add(ancestorId));
    const node = nodesById.get(id);
    if (node && !node.isLeaf) expanded.add(id);
  });
  return expanded;
}

export function countTreeNodes(root: HierarchicalNode | null | undefined): number {
  if (!root) return 0;
  const kids = root.children ?? [];
  return 1 + kids.reduce((sum, child) => sum + countTreeNodes(child), 0);
}
